import React, { useState } from 'react'
import { TimePicker as MuiTimePicker, TimePickerProps } from '@mui/x-date-pickers'
import { TextField, IconButton } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useField, useFormikContext } from 'formik'
import { BZTime } from '@e3dc-react/icons'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { DEFAULT_HUMAN_TIME } from '@e3dc-react/shell/Libs/date'

const useStyles = makeStyles()(theme => ({
  iconButton: {
    '& .MuiSvgIcon-root': {
      color: theme.palette.grey.grey00,
      cursor: 'pointer',
    },
  },
}))

interface Props extends Omit<Partial<TimePickerProps<Moment, Moment>>, 'name'> {
  name: string
  disabled?: boolean
  className?: string
}

const TimePicker: React.FC<Props> = ({ name, disabled = false, className, ...otherProps }) => {
  const { classes } = useStyles()
  const { setFieldValue } = useFormikContext()
  const [, meta] = useField(name)
  const [isOpen, setIsOpen] = useState(false)

  const momentValue = meta.value ? moment(meta.value, DEFAULT_HUMAN_TIME) : null

  const handleChange = (value: Moment | null): void => {
    setFieldValue(name, value?.isValid() ? value.format(DEFAULT_HUMAN_TIME) : '')
  }

  return (
    <MuiTimePicker
      {...otherProps}
      value={momentValue}
      onChange={handleChange}
      disabled={disabled}
      ampm={false}
      views={['hours', 'minutes']}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      renderInput={params => (
        <TextField
          {...params}
          className={className}
          fullWidth
          size="small"
          margin="dense"
          variant="outlined"
          error={!!meta.error && !!meta.touched}
          helperText={meta.touched && meta.error ? meta.error : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <IconButton onClick={() => setIsOpen(prev => !prev)} className={classes.iconButton} disabled={disabled}>
                <BZTime />
              </IconButton>
            ),
          }}
        />
      )}
    />
  )
}

TimePicker.displayName = 'TimePicker'

export default TimePicker
